import { Field, useField } from 'formik';
import { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Alert } from '@mui/material';
import Highlight from 'react-highlight';

import 'highlight.js/styles/tomorrow-night.css';

import { Section } from '..';
import { DisplayRules, TShowStrategy } from '../../../../types';
import { FormRowsDivider, InputField, MultiSelect, RadioField } from '../../../fields';
import { useAppSelector } from '../../../../lib';
import { configSelectors } from '../../../../store';

import { TriggerSettings } from './trigger-settings';

type ShowStrategyProps = {};

type Option = { id: TShowStrategy; label: string };

export const ShowStrategy: FC<ShowStrategyProps> = () => {
  const [displayRulesField] = useField<DisplayRules>('campaign.displayRules');
  const { showStrategies, autoOpenDelay, renderStrategy } = displayRulesField.value;
  const hasAutoOpenStrategy = showStrategies.includes('auto-open');
  const hasClickToTriggerStrategy = showStrategies.includes('click-to-trigger');
  const hasJSEventStrategy = showStrategies.includes('open-on-event');

  const { t } = useTranslation();

  const options: Option[] = useMemo(
    () => [
      { id: 'click-to-trigger', label: t('showStrategy.clickToTrigger') },
      { id: 'auto-open', label: t('showStrategy.autoOpen') },
      { id: 'exit-intent', label: t('showStrategy.exitIntent') },
      { id: 'open-on-event', label: t('showStrategy.openOnEvent') },
    ],
    [t]
  );

  const wixOptions: Option[] = useMemo(() => [{ id: 'after-purchase', label: t('showStrategy.afterPurchase') }], [t]);

  const renderToPage = renderStrategy === 'page';

  const isWix = useAppSelector(configSelectors.isWix);

  const integrations = useMemo(() => {
    if (isWix) {
      return [...options, ...wixOptions];
    }

    return options;
  }, [isWix, options, wixOptions]);

  const renderOptions = useMemo(() => {
    return [
      { label: t('pages.campaign.showStrategy.renderOptions.popup'), value: 'popup' },
      { label: t('pages.campaign.showStrategy.renderOptions.page'), value: 'page' },
    ];
  }, [t]);

  const validate = useCallback((value: string[]) => {
    return value.length > 0;
  }, []);

  return (
    <ShowStrategyWrapper>
      <Section elevation={0} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        {!isWix && (
          <>
            <Field
              name="campaign.displayRules.renderStrategy"
              component={RadioField}
              label={t('pages.campaign.showStrategy.renderStrategy')}
              options={renderOptions}
              defaultValue="popup"
            />
            <Spacer />
            <Spacer />
          </>
        )}

        <Field
          name="campaign.displayRules.showStrategies"
          component={MultiSelect}
          label={t('pages.campaign.showStrategy.triggers')}
          options={integrations}
          className="show-strategies-select"
          validateBeforeChange={validate}
          disabled={renderToPage}
        />

        {hasClickToTriggerStrategy && !renderToPage && <TriggerSettings />}

        {hasAutoOpenStrategy && !renderToPage && (
          <>
            <FormRowsDivider />
            <Field
              name="campaign.displayRules.autoOpenDelay"
              component={InputField}
              label="Auto open delay"
              type="number"
              helperText={`The widget will be opened after ${autoOpenDelay} seconds (in popup) after site visited`}
              className="form-field"
            />
          </>
        )}

        {hasJSEventStrategy && !renderToPage &&  (
          <>
            <FormRowsDivider />

            <Alert severity="info" variant="filled">
              {t('pages.campaign.showStrategy.renderOnEventDescription')}

              <Highlight className="html insert-code">
                window.dispatchEvent(new CustomEvent('eg-open-widget'))
              </Highlight>
            </Alert>
          </>
        )}

        {renderToPage && (
          <>
            <FormRowsDivider />

            <Field
              name="campaign.displayRules.renderElementId"
              component={InputField}
              label={t('pages.campaign.showStrategy.renderElementId')}
            />

            <Spacer />

            <Alert severity="info" variant="filled">
              {t('pages.campaign.showStrategy.renderElementIdDescription')}
            </Alert>
          </>
        )}
      </Section>
    </ShowStrategyWrapper>
  );
};

const ShowStrategyWrapper = styled.div`
  .show-strategies-select {
    min-width: 290px;
    margin-bottom: 16px;
  }

  .form-field {
    width: 290px;
  }

  .row-field {
    width: 220px;
    min-width: unset;
    margin-right: 16px;

    &:last-child {
      margin-right: 0;
    }
  }
`;

const Spacer = styled.div`
  height: 12px;
`;
